import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { getScams } from '@/hooks/useScam';
import { HiOutlineSearch } from 'react-icons/hi';
import {
  Flag,
  ShoppingCart,
  Phone,
  Heart,
  HeartHandshake,
  Hand,
  Gift,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { createFileRoute, Link } from '@tanstack/react-router';
import ScamCard from '@/components/ScamCard';
import { displayCategoryName } from '@/lib/scamCategories';

/**
 * The Scam Library. Rebuilt 2026-06-12 (TD-016/17 follow-on) around how the
 * audience actually looks things up: "something just happened to me, is it a
 * scam?" One search box, a single-line category rail, "how it reached you"
 * chips, and a safety net so not finding a match never reads as "it's fine".
 * The old checkbox sidebar (risk levels, tags) goes; those facets return
 * with the structured-fields rebuild (DEC-009) if they earn their place.
 */

// scam.icon keys → tile icons (legacy data drives these values)
const ICON_OPTIONS = [
  { value: 'Cart', icon: <ShoppingCart className="h-7 w-7 text-violet-600" /> },
  { value: 'Headset', icon: <Phone className="h-7 w-7 text-violet-600" /> },
  { value: 'Heart', icon: <Heart className="h-7 w-7 text-violet-600" /> },
  { value: 'HeartWithV', icon: <HeartHandshake className="h-7 w-7 text-violet-600" /> },
  { value: 'PalmUp', icon: <Hand className="h-7 w-7 text-violet-600" /> },
  { value: 'Cart2', icon: <Gift className="h-7 w-7 text-violet-600" /> },
];


const CHANNEL_LABELS: Record<string, string> = {
  EMAIL: 'Email',
  PHONE: 'Phone call',
  SMS: 'Text message',
  SOCIAL_MEDIA: 'Social media',
  IN_PERSON: 'In person',
  ONLINE: 'Online',
  POST: 'Post',
};

export const Route = createFileRoute('/_auth/scam-library/')({
  component: RouteComponent,
});

/**
 * Single-line category rail. Categories will keep growing, so they must never
 * stack into a pill cloud: one row, horizontal scroll, chevrons on desktop
 * (older users rarely discover horizontal scroll without a visible control).
 */
function CategoryRail({
  categories,
  selected,
  onSelect,
}: {
  categories: { id: string; name: string }[];
  selected: string | null;
  onSelect: (id: string | null) => void;
}) {
  const railRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = railRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    updateScrollState();
    window.addEventListener('resize', updateScrollState);
    return () => window.removeEventListener('resize', updateScrollState);
  }, [updateScrollState, categories.length]);

  const nudge = (direction: 1 | -1) => {
    railRef.current?.scrollBy({ left: direction * 320, behavior: 'smooth' });
  };

  const pillClass = (active: boolean) =>
    `inline-flex min-h-11 shrink-0 cursor-pointer items-center rounded-full border px-4 py-2 text-[15px] font-medium transition-colors ${
      active
        ? 'border-slate-900 bg-slate-900 text-white'
        : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
    }`;

  return (
    <div className="relative mt-6">
      <div
        ref={railRef}
        onScroll={updateScrollState}
        className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0 scrollbar-hide"
      >
        <div className="flex gap-2" role="group" aria-label="Filter by category">
          <button
            type="button"
            onClick={() => onSelect(null)}
            aria-pressed={selected === null}
            className={pillClass(selected === null)}
          >
            All scams
          </button>
          {categories.map((cat) => {
            const active = selected === cat.id;
            return (
              <button
                key={cat.id}
                type="button"
                onClick={() => onSelect(active ? null : cat.id)}
                aria-pressed={active}
                className={pillClass(active)}
              >
                {displayCategoryName(cat.name)}
              </button>
            );
          })}
        </div>
      </div>

      {/* Edge fades + chevrons, only when there is more to see */}
      {canScrollLeft && (
        <>
          <div className="pointer-events-none absolute inset-y-0 left-0 hidden w-16 bg-gradient-to-r from-[#f5f5f7] to-transparent sm:block" />
          <button
            type="button"
            onClick={() => nudge(-1)}
            aria-label="Scroll categories left"
            className="absolute left-0 top-1/2 hidden h-9 w-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm transition-colors hover:text-slate-900 sm:flex"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
        </>
      )}
      {canScrollRight && (
        <>
          <div className="pointer-events-none absolute inset-y-0 right-0 hidden w-16 bg-gradient-to-l from-[#f5f5f7] to-transparent sm:block" />
          <button
            type="button"
            onClick={() => nudge(1)}
            aria-label="Scroll categories right"
            className="absolute right-0 top-1/2 hidden h-9 w-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm transition-colors hover:text-slate-900 sm:flex"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </>
      )}
    </div>
  );
}

function ScamRowSkeleton() {
  return (
    <div className="flex items-center gap-5 px-4 py-4 sm:px-6 sm:py-5">
      <div className="h-16 w-16 sm:h-24 sm:w-24 shrink-0 rounded-2xl bg-slate-100" />
      <div className="flex-1 space-y-2.5 py-1">
        <div className="h-5 w-2/5 rounded bg-slate-100" />
        <div className="h-4 w-full rounded bg-slate-100/70" />
        <div className="h-4 w-3/4 rounded bg-slate-100/70" />
      </div>
    </div>
  );
}

function RouteComponent() {
  const { data: scams, isLoading, isError, refetch } = getScams();
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);
  const searchParams = Route.useSearch();
  const invitationcode = searchParams.invitationcode;
  const inputRef = useRef<HTMLInputElement>(null);
  const commonSearches = ['delivery fee', 'refund', 'remote access', 'bank call', 'WhatsApp code'];

  const availableCategories = useMemo(() => {
    if (!scams) return [];
    const counts = new Map<string, { name: string; count: number }>();
    for (const s of scams) {
      const cid = (s as any).scamCategoryId || (s as any).scamCategory?.id;
      const cname = (s as any).scamCategory?.name;
      if (!cid) continue;
      const entry = counts.get(cid) || { name: cname || 'Other', count: 0 };
      entry.count += 1;
      counts.set(cid, entry);
    }
    return Array.from(counts.entries())
      .map(([id, { name, count }]) => ({ id, name, count }))
      .sort((a, b) => b.count - a.count);
  }, [scams]);

  const availableChannels = useMemo(() => {
    if (!scams) return [];
    const set = new Set<string>();
    for (const s of scams) for (const c of s.channels) set.add(c);
    return Array.from(set)
      .filter((c) => CHANNEL_LABELS[c])
      .sort();
  }, [scams]);

  const hasActiveFilters = selectedCategory !== null || selectedChannels.length > 0;

  const filteredScams = useMemo(() => {
    if (!scams) return [];
    const term = search.toLowerCase();
    return scams.filter((scam) => {
      const matchesText =
        scam.title.toLowerCase().includes(term) || scam.description.toLowerCase().includes(term);
      const scamCatId = (scam as any).scamCategoryId || (scam as any).scamCategory?.id;
      const matchesCategory = !selectedCategory || scamCatId === selectedCategory;
      const matchesChannels =
        selectedChannels.length === 0 || selectedChannels.some((ch) => scam.channels.includes(ch));
      return matchesText && matchesCategory && matchesChannels;
    });
  }, [scams, search, selectedCategory, selectedChannels]);

  const clearAll = () => {
    setSearch('');
    setSelectedCategory(null);
    setSelectedChannels([]);
  };

  const toggleChannel = (ch: string) => {
    setSelectedChannels((prev) =>
      prev.includes(ch) ? prev.filter((c) => c !== ch) : [...prev, ch],
    );
  };

  return (
    <div className="min-h-screen bg-[#f5f5f7]">
      <div className="mx-auto max-w-6xl px-4 pt-8 pb-16 sm:px-6 sm:pt-10">
        {/* Header */}
        <div className="mb-7 flex items-start justify-between gap-4 px-1">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
              The Scam Library
            </h1>
            <p className="mt-2 max-w-[50ch] text-[17px] leading-relaxed text-slate-600">
              Real scams, explained: how they work and the warning signs to
              watch for.
            </p>
          </div>
          <Link
            to="/report-a-scam"
            search={{ invitationcode, __clerk_ticket: undefined }}
            className="hidden sm:inline-flex shrink-0 items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:border-violet-300 hover:bg-violet-50 hover:text-violet-700"
          >
            <Flag className="h-4 w-4" />
            Report a scam
          </Link>
        </div>

        {/* Search */}
        <div className="relative">
          <HiOutlineSearch className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            ref={inputRef}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search the scam library"
            placeholder="Search by what happened, e.g. 'parcel fee' or 'bank call'"
            className="w-full rounded-2xl border border-slate-200 bg-white py-3.5 pl-12 pr-4 text-[17px] text-slate-900 shadow-card outline-none transition-colors placeholder:text-slate-400 focus:border-violet-400 focus:ring-2 focus:ring-violet-100"
          />
        </div>

        {/* Quick searches */}
        <div className="mt-3 flex flex-wrap items-center gap-2 px-1">
          <span className="text-sm text-slate-600">Try:</span>
          {commonSearches.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => {
                setSearch(s);
                inputRef.current?.focus();
              }}
              className="cursor-pointer rounded-full bg-white border border-slate-200 px-3 py-1.5 text-sm text-slate-600 transition-colors hover:border-violet-300 hover:text-violet-700"
            >
              {s}
            </button>
          ))}
        </div>

        {/* Category rail */}
        {availableCategories.length > 0 && (
          <CategoryRail
            categories={availableCategories}
            selected={selectedCategory}
            onSelect={setSelectedCategory}
          />
        )}

        {/* Channel chips */}
        {availableChannels.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2 px-1" role="group" aria-label="Filter by how it reached you">
            <span className="text-sm text-slate-600">How it reached you:</span>
            {availableChannels.map((ch) => {
              const active = selectedChannels.includes(ch);
              return (
                <button
                  key={ch}
                  type="button"
                  onClick={() => toggleChannel(ch)}
                  aria-pressed={active}
                  className={`cursor-pointer rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
                    active
                      ? 'border-violet-600 bg-violet-600 text-white'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-violet-300 hover:text-violet-700'
                  }`}
                >
                  {CHANNEL_LABELS[ch]}
                </button>
              );
            })}
          </div>
        )}

        {/* Results */}
        <div className="mt-7">
          {isLoading ? (
            <div
              className="animate-pulse divide-y divide-slate-100 overflow-hidden rounded-card border border-slate-200/80 bg-white shadow-card"
              aria-label="Loading scams"
            >
              {Array.from({ length: 5 }).map((_, i) => (
                <ScamRowSkeleton key={i} />
              ))}
            </div>
          ) : isError ? (
            <div className="rounded-card border border-slate-200/80 bg-white px-6 py-12 text-center shadow-card">
              <p className="text-[17px] text-slate-700">
                We couldn't load the library just now.
              </p>
              <button
                type="button"
                onClick={() => refetch()}
                className="mt-4 inline-flex min-h-11 cursor-pointer items-center rounded-full bg-violet-600 px-6 py-2.5 text-base font-medium text-white transition-colors hover:bg-violet-700"
              >
                Try again
              </button>
            </div>
          ) : (
            <>
              <p className="mb-3 px-1 text-[15px] text-slate-600" aria-live="polite">
                {filteredScams.length === 0
                  ? 'No matching scams'
                  : `${filteredScams.length} scam${filteredScams.length === 1 ? '' : 's'}`}
                {(search || hasActiveFilters) && scams ? ` of ${scams.length}` : ''}
              </p>

              {filteredScams.length === 0 ? (
                <div className="rounded-card border border-slate-200/80 bg-white px-6 py-12 text-center shadow-card">
                  <p className="text-[17px] text-slate-700">
                    Nothing in the library matches{search ? ` "${search}"` : ' those filters'}.
                  </p>
                  <button
                    type="button"
                    onClick={clearAll}
                    className="mt-3 cursor-pointer text-base font-medium text-violet-700 underline underline-offset-4 hover:text-violet-800"
                  >
                    Clear search and filters
                  </button>
                </div>
              ) : (
                /* One grouped list, hairline-divided rows — a stack of
                   individual floating cards read as wallpaper at this length */
                <div className="divide-y divide-slate-100 overflow-hidden rounded-card border border-slate-200/80 bg-white shadow-card">
                  {filteredScams.map((scam) => (
                    <ScamCard
                      key={scam.id}
                      scam={scam}
                      iconOptions={ICON_OPTIONS}
                      search={{ invitationcode, __clerk_ticket: undefined }}
                    />
                  ))}
                </div>
              )}

              {/* Safety net: not finding it must never read as "it's fine". */}
              <div className="mt-8 rounded-card bg-violet-50 px-6 py-5">
                <p className="text-[17px] font-medium text-violet-900">
                  Can't find what's happening to you?
                </p>
                <p className="mt-1 text-base leading-relaxed text-violet-800">
                  New scams appear all the time, so it could still be one.{' '}
                  <Link
                    to="/tools"
                    search={{ invitationcode, __clerk_ticket: undefined }}
                    className="font-medium underline underline-offset-4 hover:text-violet-950"
                  >
                    Check it with the Scam Checker
                  </Link>{' '}
                  or{' '}
                  <Link
                    to="/contact-support"
                    search={{ invitationcode, __clerk_ticket: undefined }}
                    className="font-medium underline underline-offset-4 hover:text-violet-950"
                  >
                    ask a real person
                  </Link>
                  .
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
